"use client";

import { useLocale } from "@calcom/lib/hooks/useLocale";
import { useEffect } from "react";
import { createPortal } from "react-dom";

import type { KnopkaBookingPageAppearance } from "../knopkaBookingPageAppearance";
import { KnopkaBookingPageAppearanceSettingsPanel } from "./KnopkaBookingPageAppearanceSettingsPanel";

type Props = {
  username: string;
  onSaved: (appearance: KnopkaBookingPageAppearance) => void;
  onClose: () => void;
};

export function KnopkaBookingPageAppearanceEditor({ username, onSaved, onClose }: Props) {
  const { t } = useLocale();

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div className="knopka-page-appearance-overlay fixed inset-0 z-[200] flex items-end justify-center p-0 sm:items-center sm:p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/45 backdrop-blur-[2px] transition-opacity"
        aria-label={t("close")}
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="knopka-page-appearance-dialog-title"
        className="knopka-page-appearance-editor border-subtle relative z-10 flex max-h-[min(92vh,760px)] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl border shadow-2xl sm:rounded-2xl">
        <div className="overflow-y-auto overscroll-contain p-4 sm:p-5">
          <KnopkaBookingPageAppearanceSettingsPanel
            username={username}
            variant="floating"
            onClose={onClose}
            onSaved={onSaved}
          />
        </div>
      </div>
    </div>,
    document.body
  );
}
